import _ from 'lodash';
import React from 'react';

import { clinicIsPremium } from 'utils/helpers';

import { COUNTRY } from '../../../common/constants';

// Components
import ConcernsTreatmentStep from '../components/patient/ConcernsTreatmentStep/ConcernsTreatmentStep';
import ConfirmationStep from '../components/patient/ConfirmationStep/ConfirmationStep';
import CreditCardStep from '../components/patient/CreditCardStep/CreditCardStep';
import InsuranceStep from '../components/patient/InsuranceStep/InsuranceStep';
import OngoingCareStep from '../components/patient/OngoingCareStep/OngoingCareStep';
import PatientStep from '../components/patient/PatientStep/PatientStep';
import PhoneStep from '../components/patient/PhoneStep/PhoneStep';
import { ReturnError } from '../components/patient/ReturnError/ReturnError';
import VerifyStep from '../components/patient/VerifyStep/VerifyStep';

export const PATIENT_CHECKOUT_STEPS = [
  {
    name: 'patient',
    progressTitle: 'Info',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '420px';
    },
    bookingStep: false,
    state(data) {
      return {
        dateOfBirth: (_.get(data, 'patient.dateOfBirth')
          && data.patient.dateOfBirth.split('-')) || [
          data.formData.birthYear,
          data.formData.birthMonth,
          data.formData.birthDay,
        ],
        detailedInsuranceMode: data.requestedAppointment.slotId !== undefined,
        email: (data.user && data.user.email) || data.formData.email,
        firstName:
          (data.patient && data.patient.firstName) || data.formData.firstName,
        lastName:
          (data.patient && data.patient.lastName) || data.formData.lastName,
        mobilePhone:
          (data.patient && data.patient.mobilePhone)
          || data.formData.mobilePhone,
      };
    },
    isHidden() {
      // this should always be false
      return false;
    },
    hideFromProgressBar() {
      return false;
    },
    validate() {
      return true;
    },
    render(handleNextStep, state) {
      return <PatientStep handleNextStep={handleNextStep} state={state} />;
    },
  },
  {
    name: 'phone',
    progressTitle: 'Phone',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '420px';
    },
    bookingStep: false,
    state(data) {
      return {
        mobilePhone:
          (data.patient && data.patient.mobilePhone)
          || data.formData.mobilePhone,
      };
    },
    hideFromProgressBar() {
      return true;
    },
    isHidden(data) {
      // Absolutely do not attempt to hide this step if we're already on it
      if (window.location.href.indexOf('/checkout/phone') > -1) {
        return false;
      }

      // Conditions determining to hide step:
      // if there is no patient on the CheckoutFormData in redux
      // if patient already has phone
      return !_.get(data, 'patient') || _.get(data, 'patient.mobilePhone');
    },
    render(handleNextStep, state) {
      return <PhoneStep handleNextStep={handleNextStep} state={state} />;
    },
    validate() {
      return true;
    },
  },
  {
    name: 'ongoingCare',
    progressTitle: 'Ongoing Care',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '650px';
    },
    bookingStep: false,
    state() {
      return false;
    },
    isHidden(data) {
      // Don't show if match ID exists or clinic is not premium
      // I.e. only show if match ID doesn't exist and clinic is premium
      return !!data.match.id || !clinicIsPremium(data.clinic);
    },
    hideFromProgressBar() {
      return true;
    },
    validate() {
      return true;
    },
    render(handleNextStep) {
      return <OngoingCareStep handleNextStep={handleNextStep} />;
    },
  },
  {
    name: 'treatment',
    progressTitle: 'Treatment',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '650px';
    },
    bookingStep: false,
    state() {
      return false;
    },
    isHidden(data) {
      // Don't show if match ID exists or clinic is not premium
      // I.e. only show if match ID doesn't exist and clinic is premium
      return !!data.match.id || !clinicIsPremium(data.clinic);
    },
    hideFromProgressBar() {
      return true;
    },
    validate() {
      return true;
    },
    render(handleNextStep) {
      return <ConcernsTreatmentStep handleNextStep={handleNextStep} />;
    },
  },
  {
    name: 'insurance',
    progressTitle: 'Insurance',
    alternateProgressTitle(data) {
      if (_.get(data.match, 'other.hasInsurance') === 'No') {
        return 'Patient';
      }
      return null;
    },
    modalWidth(data) {
      if (!_.get(data, 'match.other.insurance.provider')) {
        return '420px';
      }
      return '800px';
    },
    bookingStep: false,
    state(data) {
      return {
        email: (data.user && data.user.email) || data.formData.email,
        mobilePhone:
          (data.patient && data.patient.mobilePhone)
          || data.formData.mobilePhone,
        firstName:
          (data.patient && data.patient.firstName) || data.formData.firstName,
        lastName:
          (data.patient && data.patient.lastName) || data.formData.lastName,
        dateOfBirth: (_.get(data, 'patient.dateOfBirth')
          && data.patient.dateOfBirth.split('-')) || [
          data.formData.birthYear,
          data.formData.birthMonth,
          data.formData.birthDay,
        ],
        memberId: data.formData.memberId,
        match: data?.match,
      };
    },
    isHidden() {
      return false;
    },
    hideFromProgressBar() {
      return false;
    },
    validate() {
      return true;
    },
    render(handleNextStep, state) {
      return <InsuranceStep handleNextStep={handleNextStep} state={state} />;
    },
  },
  {
    name: 'verify',
    progressTitle: 'Verify',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '420px';
    },
    bookingStep: false,
    state(data) {
      return {
        email: (data.patient && data.patient.email) || data.formData.email,
        mobilePhone:
          (data.patient && data.patient.mobilePhone)
          || data.formData.mobilePhone,
      };
    },
    hideFromProgressBar() {
      return true;
    },
    isHidden(data) {
      // Conditions determining to hide step:
      // if there is no patient on the CheckoutFormData in redux
      // if the user has already verified both their phone and email
      return (
        // !_.get(data, 'patient') || // This was allowing refreshes to bypass verification
        !!(
          _.get(data, 'patient.phoneVerifiedAt')
          && _.get(data, 'patient.emailVerifiedAt')
        )
      );
    },
    render(handleNextStep, state) {
      return <VerifyStep handleNextStep={handleNextStep} state={state} />;
    },
    validate() {
      return true;
    },
  },
  {
    name: 'creditcard',
    progressTitle: 'Payment',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '420px';
    },
    bookingStep: false,
    state() {
      return false;
    },
    isHidden(data) {
      // Conditions determining to hide step:
      // Hide for everyone EXCEPT American users who don't have insurance
      return !(
        _.get(data, 'match.location.address.country')
        === COUNTRY.unitedStates
        && _.get(data, 'match.other.hasInsurance') === 'No'
      );
    },
    hideFromProgressBar() {
      return true;
    },
    validate() {
      return true;
    },
    render(handleNextStep) {
      return <CreditCardStep handleNextStep={handleNextStep} />;
    },
  },
  {
    name: 'confirm',
    progressTitle: 'Confirm',
    alternateProgressTitle() {
      return null;
    },
    modalWidth() {
      return '420px';
    },
    bookingStep: true,
    state: () => false,
    isHidden() {
      // this should always be false
      return false;
    },
    hideFromProgressBar() {
      return false;
    },
    validate() {
      return true;
    },
    render(handleNextStep) {
      return <ConfirmationStep handleNextStep={handleNextStep} />;
    },
  },
  {
    name: 'return-error',
    progressTitle: 'Something went wrong',
    alternateProgressTitle: () => null,
    modalWidth: () => '420px',
    bookingStep: false,
    state: (data) => data,
    isHidden: () => !window.location.href.includes('/checkout/return-error'),
    hideFromProgressBar: () => true,
    validate: () => true,
    render: (__, state, close) => <ReturnError state={state} close={close} />,
  },
];
